// src/hooks/useNotificationState.js
// Firestore-based notification state management for client portal
// Tracks dismissed notifications and "last seen" counts for dynamic notifications

import { useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Storage key for localStorage fallback
 * Used when Firestore is unavailable or for quick initial load
 */
const LOCAL_STORAGE_KEY = "rsh_notification_state";

/**
 * Get today's date string in YYYY-MM-DD format (ET timezone)
 * Used for session-based dismissals that reset daily
 * @returns {string} Today's date string
 */
const getTodayDateString = () => {
  return new Date().toLocaleDateString("en-CA", {
    timeZone: "America/New_York",
  });
};

/**
 * Get cached state from localStorage for quick initial load
 * @returns {Object|null} Cached state or null
 */
const getCachedState = () => {
  try {
    const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

/**
 * Cache state to localStorage for quick subsequent loads
 * @param {Object} state - State to cache
 */
const cacheState = (state) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Error caching notification state:", error);
  }
};

/**
 * Default initial state for new users
 * @returns {Object} Initial notification state
 */
const getInitialState = () => ({
  lastVisitedAt: new Date().toISOString(),
  lastSeenPhotoCount: 0,
  lastSeenDocumentCount: 0,
  dismissedNotifications: {},
  createdAt: new Date().toISOString(),
});

/**
 * useNotificationState Hook
 * 
 * Manages user notification state in Firestore with localStorage caching.
 * Tracks:
 * - Last visited timestamp
 * - Last seen photo count (for "X new photos" notifications)
 * - Last seen document count (for "X new documents" notifications)
 * - Dismissed notifications with timestamps and session dates
 * 
 * @param {string} userId - Firebase Auth user ID
 * @returns {Object} Hook return object with state and methods
 * 
 * @example
 * const { 
 *   state, 
 *   loading, 
 *   dismissNotification, 
 *   isNotificationDismissed,
 *   getNewItemCount,
 *   updateLastVisited 
 * } = useNotificationState(user?.uid);
 */
export function useNotificationState(userId) {
  // State for notification preferences
  const [state, setState] = useState(() => getCachedState());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch user's notification state from Firestore on mount
   * Falls back to creating new state for first-time users
   */
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchState = async () => {
      try {
        const docRef = doc(db, "userPreferences", userId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setState(data);
          cacheState(data);
        } else {
          // Initialize for new users
          const initialState = getInitialState();
          await setDoc(docRef, initialState);
          setState(initialState);
          cacheState(initialState);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching notification state:", err);
        setError(err);
        // Fall back to cached state if available
        const cached = getCachedState();
        if (cached) {
          setState(cached);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchState();
  }, [userId]);

  /**
   * Dismiss a notification and optionally update "last seen" counts
   * 
   * @param {string} notificationId - Unique identifier for the notification
   * @param {Object} options - Dismissal options
   * @param {string} options.dismissType - 'permanent' or 'session'
   * @param {number} [options.photoCount] - Current photo count (for photo notifications)
   * @param {number} [options.documentCount] - Current document count (for document notifications)
   * 
   * @example
   * // Dismiss a photo notification and update last seen count
   * dismissNotification('progress-photos-25', {
   *   dismissType: 'permanent',
   *   photoCount: 25
   * });
   * 
   * // Dismiss a session-based notification (resets after midnight)
   * dismissNotification('selections-due-soon', {
   *   dismissType: 'session'
   * });
   */
  const dismissNotification = useCallback(
    async (notificationId, options = {}) => {
      if (!userId) return;

      const { dismissType = "permanent", photoCount, documentCount } = options;

      try {
        const docRef = doc(db, "userPreferences", userId);
        
        // Build the dismissal record
        const dismissalRecord = {
          dismissedAt: new Date().toISOString(),
          dismissType,
        };

        // Add session date for session-based dismissals
        if (dismissType === "session") {
          dismissalRecord.sessionDate = getTodayDateString();
        }

        // Build updates object
        const updates = {
          [`dismissedNotifications.${notificationId}`]: dismissalRecord,
        };

        // Update lastSeenPhotoCount if dismissing a photo notification
        if (photoCount !== undefined) {
          updates.lastSeenPhotoCount = photoCount;
        }

        // Update lastSeenDocumentCount if dismissing a document notification
        if (documentCount !== undefined) {
          updates.lastSeenDocumentCount = documentCount;
        }

        await updateDoc(docRef, updates);

        // Update local state
        setState((prev) => {
          const newState = {
            ...prev,
            dismissedNotifications: {
              ...prev?.dismissedNotifications,
              [notificationId]: dismissalRecord,
            },
          };
          
          if (photoCount !== undefined) {
            newState.lastSeenPhotoCount = photoCount;
          }
          if (documentCount !== undefined) {
            newState.lastSeenDocumentCount = documentCount;
          }
          
          cacheState(newState);
          return newState;
        });
      } catch (err) {
        console.error("Error dismissing notification:", err);
        setError(err);
      }
    },
    [userId]
  );

  /**
   * Check if a notification is currently dismissed
   * Handles both permanent and session-based dismissals
   * 
   * @param {string} notificationId - Notification ID to check
   * @returns {boolean} Whether the notification is dismissed
   */
  const isNotificationDismissed = useCallback(
    (notificationId) => {
      if (!state?.dismissedNotifications) return false;

      const dismissal = state.dismissedNotifications[notificationId];
      if (!dismissal) return false;

      // Session dismissals expire after midnight ET
      if (dismissal.dismissType === "session") {
        return dismissal.sessionDate === getTodayDateString();
      }

      // Permanent dismissals stay until notification ID changes
      // (e.g., when photo count changes, ID becomes 'progress-photos-26')
      return true;
    },
    [state?.dismissedNotifications]
  );

  /**
   * Calculate how many new items since last dismissal
   * Used for "X new photos/documents" notifications
   * 
   * @param {string} type - 'photos' or 'documents'
   * @param {number} currentCount - Current total count
   * @returns {number} Number of new items since last seen
   * 
   * @example
   * const newPhotoCount = getNewItemCount('photos', 25); // Returns 5 if lastSeenPhotoCount was 20
   */
  const getNewItemCount = useCallback(
    (type, currentCount) => {
      if (!state) return currentCount;

      const lastSeenCount =
        type === "photos"
          ? state.lastSeenPhotoCount || 0
          : state.lastSeenDocumentCount || 0;

      return Math.max(0, currentCount - lastSeenCount);
    },
    [state]
  );

  /**
   * Update last visited timestamp
   * Called when user visits the dashboard to track session
   */
  const updateLastVisited = useCallback(async () => {
    if (!userId) return;

    try {
      const docRef = doc(db, "userPreferences", userId);
      const timestamp = new Date().toISOString();
      
      await updateDoc(docRef, {
        lastVisitedAt: timestamp,
      });

      setState((prev) => {
        const newState = { ...prev, lastVisitedAt: timestamp };
        cacheState(newState);
        return newState;
      });
    } catch (err) {
      console.error("Error updating last visited:", err);
      setError(err);
    }
  }, [userId]);

  /**
   * Reset all dismissed notifications
   * Useful for testing or admin functions
   */
  const resetDismissals = useCallback(async () => {
    if (!userId) return;

    try {
      const docRef = doc(db, "userPreferences", userId);
      await updateDoc(docRef, {
        dismissedNotifications: {},
        lastSeenPhotoCount: 0,
        lastSeenDocumentCount: 0,
      });

      setState((prev) => {
        const newState = {
          ...prev,
          dismissedNotifications: {},
          lastSeenPhotoCount: 0,
          lastSeenDocumentCount: 0,
        };
        cacheState(newState);
        return newState;
      });
    } catch (err) {
      console.error("Error resetting dismissals:", err);
      setError(err);
    }
  }, [userId]);

  return {
    /** Current notification state from Firestore */
    state,
    /** Whether state is still loading from Firestore */
    loading,
    /** Any error that occurred during Firestore operations */
    error,
    /** Dismiss a notification and update counts */
    dismissNotification,
    /** Check if a notification is dismissed */
    isNotificationDismissed,
    /** Get count of new items since last dismissal */
    getNewItemCount,
    /** Update last visited timestamp */
    updateLastVisited,
    /** Reset all dismissals (for testing) */
    resetDismissals,
  };
}

export default useNotificationState;
